// TaskExecution — Polymorphic renderer
// Reads item.itemType and renders the correct component
// Supports recursive conditional_children
// Every component receives `disabled` to toggle view/edit mode

import React from "react";
import { SubtaskItemType } from "../types";
import { ChecklistItem } from "./ChecklistItem";
import { DocumentUpload } from "./DocumentUpload";
import { Dropdown } from "./Dropdown";
import { InputField } from "./InputField";
import { RadioGroup } from "./RadioGroup";
import { RepeatableInput } from "./RepeatableInput";
import type { SubtaskItem } from "../types";

interface SubtaskRendererProps {
  item: SubtaskItem;
  disabled?: boolean;
  /** Upload-specific gate for MAKER_CHECKER tasks. When provided, overrides
   * `disabled` for DOCUMENT_UPLOAD subtasks only — all other types still use
   * `disabled`. Defaults to `disabled` when omitted. */
  uploadDisabled?: boolean;
}

export const SubtaskRenderer: React.FC<SubtaskRendererProps> = ({
  item,
  disabled = false,
  uploadDisabled,
}) => {
  switch (item.itemType) {
    case SubtaskItemType.CHECKBOX:
    case SubtaskItemType.CHECKBOX_GROUP:
    case SubtaskItemType.CHECKBOX_WITH_INPUT:
    case SubtaskItemType.ATTESTATION:
      return <ChecklistItem item={item} disabled={disabled} />;
    case SubtaskItemType.RADIO_GROUP:
      return <RadioGroup item={item} disabled={disabled} />;
    case SubtaskItemType.DROPDOWN:
      return <Dropdown item={item} disabled={disabled} />;
    case SubtaskItemType.INPUT_FIELD:
    case SubtaskItemType.TEXTAREA:
    case SubtaskItemType.NUMBER:
    case SubtaskItemType.CURRENCY:
      return <InputField item={item} disabled={disabled} />;
    case SubtaskItemType.DOCUMENT_UPLOAD:
      return <DocumentUpload item={item} disabled={uploadDisabled ?? disabled} />;
    case SubtaskItemType.REPEATABLE_INPUT:
      return <RepeatableInput item={item} disabled={disabled} />;
    default:
      return null;
  }
};

export default SubtaskRenderer;
